/**
 * Campaign domain mapping.
 *
 * Campaigns are stored as configuration objects with configType 'campaigns'
 * (see CONFIG_TYPES in src/common/types.ts and the ConfigType enum in
 * src/tower-adapter/types.ts), so they travel over the existing /configs API.
 * This module maps that generic shape onto the campaign fields the UI renders.
 */

import { ConfigurationObject } from '@features/configs/services/configs-api';
import { DEFAULT_UPLOAD_TARGET, UploadTarget } from './campaign-uploads';

export type CampaignStatus = 'active' | 'inactive';

/** Shared status presentation, so the table and the detail page cannot drift. */
export const CAMPAIGN_STATUS_INDICATOR: Record<
  CampaignStatus,
  { type: 'success' | 'stopped'; label: string }
> = {
  active: { type: 'success', label: 'Active' },
  inactive: { type: 'stopped', label: 'Inactive' },
};

export interface Campaign {
  /** Stable identifier, also the configId and the route path segment */
  campaignId: string;
  /** Human-readable name shown in the campaigns table */
  campaignName: string;
  /** Short explanation of what the campaign is for */
  description: string;
  status: CampaignStatus;
  /** Bucket folder uploads for this campaign land in. */
  target: UploadTarget;
}

/** The configType campaigns are stored under. */
export const CAMPAIGN_CONFIG_TYPE = 'campaigns';

/**
 * Maps a configuration object onto a Campaign.
 * Unknown or missing status values fall back to 'inactive' rather than throwing,
 * since payloads are user-editable JSON and may predate the status field.
 */
export function toCampaign(config: ConfigurationObject): Campaign {
  const payload = config.payload ?? {};
  // Anything that isn't explicitly 'active' is treated as inactive, so a missing
  // or malformed status disables uploads rather than enabling them.
  const raw = typeof payload.status === 'string' ? payload.status.trim().toLowerCase() : '';
  const status: CampaignStatus = raw === 'active' ? 'active' : 'inactive';

  // Constrain to the two destinations the backend accepts; anything else
  // (including an unset value) falls back to the default.
  const rawTarget = typeof payload.target === 'string' ? payload.target.trim() : '';
  const target: UploadTarget =
    rawTarget === 'input' || rawTarget === 'campaigns-input' ? rawTarget : DEFAULT_UPLOAD_TARGET;

  return {
    campaignId: config.configId,
    campaignName: config.configName,
    description: typeof payload.description === 'string' ? payload.description : '',
    status,
    target,
  };
}
